/**
 * src/components/spatial/MapAggregateLayersController.tsx
 *
 * Headless controller that feeds the Phase 2 aggregate map layers
 * (country policy, execution rules, presence density, agents, drones)
 * into `MapDataContext`, from GeoLink's normalized `/api/map/*` API.
 *
 * Cost discipline (the "on by default, unless it costs me" contract):
 *   - Only layers the user has toggled VISIBLE are fetched.
 *   - REST snapshots are bbox + zoom scoped and fire on camera-idle
 *     (debounced) plus a slow safety interval — never a tight poll.
 *   - Live updates ride the WS delta feed (one socket), so between camera
 *     moves the layers stay fresh without re-polling.
 *   - A 401/403 auto-hides that layer so we stop requesting forbidden data.
 *
 * Renders nothing — it's pure data plumbing. The deck overlay draws the
 * slices; `MapLayersPanel` toggles visibility + shows the legend.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import { useMapData } from './MapDataContext';
import { AGGREGATE_LAYERS, aggregateLayerByChannelBase, type AggregateLayerDescriptor } from './mapAggregateLayers';
import { openMapDeltaFeed, type MapDeltaHandle } from '../../services/spatial/map/mapDeltaClient';
import { mapChannel, parseMapChannel } from '../../services/spatial/map/mapChannels';
import type { MapReadParams } from '../../services/spatial/map/mapTypes';
import { isMapDeltaEnabled, getMapLayersRefreshMs } from '../../services/spatial/spatialEnv';

/**
 * GeoLink's bbox-scoped map endpoints reject a viewport whose bbox area
 * exceeds ~100 deg² (`bbox_too_large`). Stay just under so a valid request
 * never round-trips into a 400 (mirrors SpatialObjectsMapLayer).
 */
const MAP_API_MAX_BBOX_DEG2 = 95;

/** Approximate bbox area in deg², antimeridian-aware. */
function bboxAreaDeg2([minLng, minLat, maxLng, maxLat]: [number, number, number, number]): number {
  const latSpan = Math.abs(maxLat - minLat);
  let lngSpan = maxLng - minLng;
  if (lngSpan < 0) lngSpan += 360; // viewport crosses the antimeridian
  return Math.abs(lngSpan) * latSpan;
}

export interface MapAggregateLayersControllerProps {
  provider: SpatialMapProvider;
  /** Master enable (mirrors SpatialObjects view.enabled). */
  enabled: boolean;
}

const MapAggregateLayersController: React.FC<MapAggregateLayersControllerProps> = ({
  provider,
  enabled,
}) => {
  const { state, setSlice, applyDelta, setLayerStatus, toggleLayer } = useMapData();
  const visibleLayerIds = state.visibleLayerIds;

  const deltaEnabled = useMemo(() => isMapDeltaEnabled(), []);
  const refreshMs = useMemo(() => getMapLayersRefreshMs(), []);

  // ── One-time: switch the default-visible aggregate layers on ──
  const didInitVisibility = useRef(false);
  useEffect(() => {
    if (didInitVisibility.current) return;
    didInitVisibility.current = true;
    for (const d of AGGREGATE_LAYERS) {
      if (d.defaultVisible) toggleLayer(d.layerId, true);
    }
  }, [toggleLayer]);

  // ── Which descriptors are currently visible (= should fetch) ──
  const activeDescriptors = useMemo(
    () => AGGREGATE_LAYERS.filter((d) => visibleLayerIds.includes(d.layerId)),
    [visibleLayerIds],
  );

  // ── Viewport helpers ──
  const currentBbox = useCallback((): [number, number, number, number] | null => {
    try {
      const b = provider.getBounds();
      return [b.sw.lng, b.sw.lat, b.ne.lng, b.ne.lat];
    } catch {
      return null;
    }
  }, [provider]);

  const currentZoom = useCallback((): number | undefined => {
    try { return provider.getZoom(); } catch { return undefined; }
  }, [provider]);

  // ── Per-layer REST snapshot ──
  const fetchLayer = useCallback(async (d: AggregateLayerDescriptor) => {
    const params: MapReadParams = { ...(d.params ?? {}) };

    if (d.scope === 'bbox') {
      const bbox = currentBbox();
      if (!bbox) return;
      // Skip (don't 400) when the viewport is larger than the map API allows.
      // The layer just stays empty until the user zooms in.
      if (bboxAreaDeg2(bbox) > MAP_API_MAX_BBOX_DEG2) {
        setLayerStatus(d.layerId, { loading: false, error: null, permissionDenied: false, empty: true });
        return;
      }
      params.bbox = bbox;
      params.zoom = currentZoom();
      params.limit = 1000;
    }

    setLayerStatus(d.layerId, { loading: true, error: null, permissionDenied: false });
    try {
      const fc = await d.fetch(params);
      const features = fc.features ?? [];
      setSlice(d.slice, features, {
        nextCursor: fc.pagination?.nextCursor ?? null,
        hasMore: fc.pagination?.hasMore,
      });
      setLayerStatus(d.layerId, {
        loading: false,
        error: null,
        permissionDenied: false,
        empty: features.length === 0,
      });
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      const permissionDenied = status === 401 || status === 403;
      setLayerStatus(d.layerId, {
        loading: false,
        permissionDenied,
        error: permissionDenied ? null : ((err as Error)?.message ?? 'Failed to load'),
      });
      // Stop hammering a layer the current key isn't allowed to read.
      if (permissionDenied) toggleLayer(d.layerId, false);
    }
  }, [currentBbox, currentZoom, setSlice, setLayerStatus, toggleLayer]);

  // Keep a ref to the active set so timers/handlers don't re-arm on change.
  const activeRef = useRef(activeDescriptors);
  useEffect(() => { activeRef.current = activeDescriptors; }, [activeDescriptors]);
  // Fetch active layers, optionally limited to one scope (camera moves only
  // need to refetch the viewport-scoped layers, not the global ones).
  const fetchActive = useCallback((scope?: 'bbox' | 'global') => {
    for (const d of activeRef.current) {
      if (!scope || d.scope === scope) void fetchLayer(d);
    }
  }, [fetchLayer]);
  const fetchActiveRef = useRef(fetchActive);
  useEffect(() => { fetchActiveRef.current = fetchActive; }, [fetchActive]);

  // ── Fetch when enabled and whenever the active set changes (both scopes) ──
  useEffect(() => {
    if (!enabled) return;
    fetchActiveRef.current();
  }, [enabled, activeDescriptors]);

  // ── Camera-idle (debounced): refetch only the bbox-scoped layers ──
  useEffect(() => {
    if (!enabled) return;
    const native = provider.nativeMap as {
      on?: (e: string, h: () => void) => void;
      off?: (e: string, h: () => void) => void;
    };
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => fetchActiveRef.current('bbox'), 600);
    };
    if (typeof native?.on === 'function') native.on('moveend', handler);
    // Slow safety interval refreshes everything (backstops a missed delta).
    const interval = setInterval(() => fetchActiveRef.current(), refreshMs);
    return () => {
      try { native?.off?.('moveend', handler); } catch { /* ignore */ }
      if (debounce) clearTimeout(debounce);
      clearInterval(interval);
    };
  }, [enabled, provider, refreshMs]);

  // ── WS delta feed (one socket; live upserts/removes) ──
  const handleRef = useRef<MapDeltaHandle | null>(null);
  useEffect(() => {
    if (!enabled || !deltaEnabled) return;
    const handle = openMapDeltaFeed({
      initialChannels: activeRef.current.map((d) => mapChannel(d.channelBase)),
      onEvent: (evt) => {
        const parsed = parseMapChannel(evt.channel);
        if (!parsed) return;
        const d = aggregateLayerByChannelBase(parsed.base);
        if (!d) return;
        const isRemoval = /\.(deleted|ended|offline)$/.test(evt.type);
        if (isRemoval && evt.id != null) {
          applyDelta(d.slice, undefined, [String(evt.id)]);
        } else if (evt.feature) {
          applyDelta(d.slice, [evt.feature]);
        }
      },
      onResync: (channel) => {
        const parsed = parseMapChannel(channel);
        if (!parsed) return;
        const d = aggregateLayerByChannelBase(parsed.base);
        if (d) void fetchLayer(d);
      },
    });
    handleRef.current = handle;
    return () => {
      handle.close();
      handleRef.current = null;
    };
  }, [enabled, deltaEnabled, applyDelta, fetchLayer]);

  // Keep the WS subscription set in sync with the visible layers.
  useEffect(() => {
    if (!deltaEnabled) return;
    handleRef.current?.setChannels(activeDescriptors.map((d) => mapChannel(d.channelBase)));
  }, [activeDescriptors, deltaEnabled]);

  return null;
};

export default MapAggregateLayersController;
